import {CommandRequester} from '../../../../CommandRequester';
import RequestAPI from '../../../RequestAPI';
import RequestToken from '../../../../tokens/RequestToken';
import {Event} from '../../../../events/Event';
import {MotionEvents, CommandTypes, StreamTypes} from '@jibo/command-protocol';

/**
 * @class MotionToken
 * @extends RequestToken
 * @hideconstructor
 */
export class MotionToken extends RequestToken<JIBO.v1.MotionRequest, never> {
    /**
     * @name MotionToken#update
     * @type {Event}
     */
    public update:Event<JIBO.v1.MotionEntity[]>;

    constructor(owner:CommandRequester, protocol: JIBO.v1.MotionRequest) {
        super(owner, protocol);
        this.update = new Event('Update of motion entities');
    }

    /**
     * @private
     */
    public handleAck(ack:JIBO.v1.Acknowledgement) {
        if (ack.Response.ResponseCode >= 400) {
            this.isComplete = true;
            this._complete.reject(ack.Response);
            return;
        }
    }

    /**
     * @private
     */
    public handleEvent(event:JIBO.v1.EventMessage) {
        const message = event.EventBody;
        switch (message.Event) {
            case MotionEvents.MotionDetected:
                this.update.emit(message.Motions);
                break;
        }
    }
}

/**
 * Internal Motion class
 * @class CommandRequester.perception.subscribe.Motion
 * @intdocs
 */
export class Motion extends RequestAPI<JIBO.v1.MotionRequest, MotionToken> {

    /**
     * Generate Motion Track Protocol
     * @method CommandRequester.perception.subscribe.Motion#generateProtocol
     * @returns {MotionRequest}
     * @intdocs
     */
    static generateProtocol(): JIBO.v1.MotionRequest {
        return {
            Type: CommandTypes.Subscribe,
            StreamType: StreamTypes.Motion,
            StreamFilter: {}
        };
    }

    /**
     * Create MotionToken from MotionRequest protocol.
     * @method CommandRequester.perception.subscribe.Motion#generateToken
     * @param {MotionRequest} protocol - MotionRequest protocol to generate a MotionToken from.
     * @param {boolean} [andSend=false] - `true` if the generated MotionToken should also be sent as a request.
     * @return {MotionToken}
     * @intdocs
     */
    public generateToken(protocol: JIBO.v1.MotionRequest, andSend=false): MotionToken {
        const token = new MotionToken(this.owner, protocol);
        if (andSend) {
            super.sendToken(token);
        }
        return token;
    }

    /**
     * Send MotionToken request.
     * <p>NOTE: Implementation in base class {@link RequestAPI}</p>
     * @method CommandRequester.perception.subscribe.Motion#sendToken
     * @param {MotionToken} token - MotionToken to send.
     * @intdocs
     */
}